import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import EmojiObjectsOutlinedIcon from '@mui/icons-material/EmojiObjectsOutlined'
import { useSnackbar } from 'notistack'

import { CircularProgress } from '@material-ui/core'

import Icon from '../../atoms/display/icon'

import useStyles from './summarizationWidgetStyle'

import SummarizationIcon from '@mui/icons-material/AutoFixHigh'
import Button from '../../atoms/inputs/button'
import SummaryContent from '../../molecules/summary/summaryContent'
import SummaryAccordion from '../../molecules/summary/summaryAccordion'
import { useGenerateSummary } from '../../../hooks/useGenerateSummary'

const SummarizationWidget = () => {
  const { t } = useTranslation()

  const activeConversation = useSelector((state) => state.activeConversation)
  const {
    dataSummaries,
    loadingGetSummaries,
    loadingGenerateSummary,
    conversationId,
  } = useSelector((state) => state.summaries)

  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()

  const onError = useCallback(() => {
    enqueueSnackbar(t('summary.Error summarizing conversation'), {
      variant: 'error',
      autoHideDuration: 3000,
    })
  }, [enqueueSnackbar, t])

  const { generateSummary, fetchAllSummaries } = useGenerateSummary({
    conversationId: activeConversation,
    onSuccess: () => {},
    onError,
  })

  useEffect(() => {
    if (activeConversation !== conversationId) {
      fetchAllSummaries()
    }
  }, [activeConversation])

  const handleCollapse = useCallback((index) => {
    setExpanded((expanded) => ({
      ...expanded,
      [index]: expanded[index] === true ? false : true,
    }))
  }, [])

  const [expanded, setExpanded] = useState(
    dataSummaries.reduce((acc, { id }) => ({ ...acc, [id]: false }), {})
  )

  const shortcuts = useSelector((state) => state.shortcuts)

  const memorizedContentSummary = useMemo(() => {
    return loadingGetSummaries || loadingGenerateSummary ? (
      <div className={classes.centerContent}>
        <CircularProgress></CircularProgress>
        {t('summary.readingContent')}
      </div>
    ) : (
      <>
        <div className={classes.content}>
          {dataSummaries &&
            dataSummaries.length > 0 &&
            dataSummaries.map((summary, index) => (
              <SummaryAccordion
                key={summary.id}
                id={summary.id}
                expanded={expanded}
                handleCollapse={handleCollapse}
                title={summary.created_at}
                subtitle={summary.created_by_user_name}
              >
                <div style={{ marginBottom: '16px' }}>
                <SummaryContent
                  summaryText={summary.summary}
                  keywords={summary.keywords}
                ></SummaryContent>
                </div>
              </SummaryAccordion>
            ))}
        </div>
        <div style={{ textAlign: 'left', padding: '0px 10px' }}>
          <Button
            size="large"
            variant="contained"
            color={'primary'}
            fullWidth={false}
            onClick={generateSummary}
            disabled={false}
            style={{ borderRadius: '50px' }}
          >
            {t('buttons.summarizeConversation')}
          </Button>
        </div>
      </>
    )
  }, [
    loadingGetSummaries,
    loadingGenerateSummary,
    dataSummaries,
    expanded,
    handleCollapse,
    generateSummary,
    shortcuts,
    t,
  ])

  return (
    <>
      <div className={classes.root}>
        <div className={classes.right}>
          <div className={classes.rightHeader}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Icon text={t('summary.conversationSummary')}>
                <SummarizationIcon />
              </Icon>
            </div>
          </div>
          <div
            style={{
              padding: '10px',
            }}
          >
            <div className={classes.summaryInfo}>
              <div>
                <Icon>
                  <EmojiObjectsOutlinedIcon></EmojiObjectsOutlinedIcon>
                </Icon>
              </div>
              <div
               className={classes.summaryInfoText}
              >
                {t('summary.info')}
              </div>
            </div>
          </div>
          {memorizedContentSummary}
        </div>
      </div>
    </>
  )
}

export default SummarizationWidget
